import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { FC, useRef, useState, useTransition } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import moment from 'moment';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { getDayEnd, getDayStart, useDataProvider } from 'src/app/common/utils';

import {
  ACCESSIBILITY_OPTIONS,
  ACTIVITY_TRACKING_BASE_PATH,
  FRAMEWORK_OPTIONS,
  QR_CODE_ACTIVITY_CATEGORY_PATH,
  STATUS_OPTIONS,
} from '../../../constans';
import {
  fetchQRCodeActivityCategoryList,
  updateStatusQRCodeActivityCategory,
} from '../../../network/activityTrackingCrud';
import {
  QRCodeActivityCategoryApiPaginateList,
  QRCodeActivityCategoryApiResponse,
} from '../../../types/activity-tracking-api-types';
import { EStatus, QRCodeActivityCategoryListParam } from '../../../types/activity-tracking-types';

type QRCodeActivityCategoryListPageProps = { initialParams?: QRCodeActivityCategoryListParam } & ParamsProps &
  ModulePermissionProps;

const initialState: QRCodeActivityCategoryListParam = {
  name: '',
  accessibility: '',
  framework: '',
  createdAtFrom: null,
  createdAtTo: null,
  status: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: QRCodeActivityCategoryListParam): QRCodeActivityCategoryListParam => {
  return initialParams
    ? {
        name: initialParams.name || '',
        accessibility: initialParams.accessibility || '',
        framework: initialParams.framework || '',
        createdAtFrom: initialParams.createdAtFrom ? getDayStart(new Date(initialParams.createdAtFrom)) : null,
        createdAtTo: initialParams.createdAtTo ? getDayEnd(new Date(initialParams.createdAtTo)) : null,
        status: initialParams.status || '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const QRCodeActivityCategoryListPage: FC<QRCodeActivityCategoryListPageProps> = ({
  initialParams,
  onChangeQueryParams,
  enableCreate,
  enableUpdate,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [filterState, setFilterState] = useState<QRCodeActivityCategoryListParam>(paramsInitiator(initialParams));
  const [categoryList, setCategoryList] = useState<QRCodeActivityCategoryApiPaginateList>();
  const filterStateRef = useRef(filterState);
  const pinnedItemRef = useRef<QRCodeActivityCategoryApiResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [_, startTransition] = useTransition();

  const { isLoading, refreshData } = useDataProvider<QRCodeActivityCategoryApiPaginateList>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterStateRef.current);
        }
        const result = await fetchQRCodeActivityCategoryList(filterStateRef.current, dispatch);
        if (pinnedItemRef.current && filterStateRef.current.page === 1) {
          const pinned = pinnedItemRef.current;
          const filteredItems = result.items.filter((i) => i._id !== pinned._id);
          return { ...result, items: [pinned, ...filteredItems].slice(0, filterStateRef.current.limit ?? 5) };
        }
        return result;
      } catch (err) {}
    },
    setCategoryList,
    false,
  );

  const handleToggleStatus = async (category: QRCodeActivityCategoryApiResponse, status: EStatus) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await updateStatusQRCodeActivityCategory(
        category._id ?? '',
        {
          ...category,
          status: status,
        },
        dispatch,
        abortControllerRef.current.signal,
      );

      if (!response.isSuccess) {
        const error = response.error;
        if (error?.code === 'SORT_ORDER_DUPLICATE_MESSAGE') {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: `Display Order number are used. Please input another number.`,
              },
            ]),
          );
          return;
        }
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: `${
                status === EStatus.Active
                  ? Translation('activityTracking.notify.publishFailed')
                  : Translation('activityTracking.notify.unpublishFailed')
              } - ${category.name}`,
            },
          ]),
        );
        return;
      }
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `${
              status === EStatus.Active
                ? Translation('activityTracking.notify.publishSuccess')
                : Translation('activityTracking.notify.unpublishSuccess')
            } - ${category.name}`,
          },
        ]),
      );
      refreshData();
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${
              status === EStatus.Active
                ? Translation('activityTracking.notify.publishFailed')
                : Translation('activityTracking.notify.unpublishFailed')
            } - ${category.name}`,
          },
        ]),
      );
    }
  };

  const detailPath = `${ACTIVITY_TRACKING_BASE_PATH}${QR_CODE_ACTIVITY_CATEGORY_PATH}`;

  return (
    <>
      <PruFilter
        title={Translation('activityTracking.qrCodeActivityCategory.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterState.name,
            displayName: Translation('activityTracking.qrCodeActivityCategory.category'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'accessibility',
            initialValue: filterState.accessibility,
            displayName: Translation('activityTracking.qrCodeActivityCategory.accessibility'),
            choices: ACCESSIBILITY_OPTIONS,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'framework',
            initialValue: filterState.framework,
            displayName: Translation('activityTracking.qrCodeActivityCategory.framework'),
            choices: FRAMEWORK_OPTIONS,
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'createdAtFrom',
            fieldTo: 'createdAtTo',
            initialDateFrom: filterState.createdAtFrom,
            initialDateTo: filterState.createdAtTo,
            displayName: Translation('activityTracking.qrCodeActivityCategory.createdTime'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            initialValue: filterState.status,
            displayName: Translation('activityTracking.qrCodeActivityCategory.status'),
            choices: STATUS_OPTIONS,
          },
        ]}
        onChangeFilter={(data) => {
          pinnedItemRef.current = null;
          const next = {
            ...filterState,
            name: data.name,
            accessibility: data.accessibility,
            framework: data.framework,
            createdAtFrom: data.createdAtFrom,
            createdAtTo: data.createdAtTo,
            status: data.status,
          };
          filterStateRef.current = next;
          setFilterState(next);
        }}
        fetchData={refreshData}
      />

      <PruTable
        disableBulkSelect
        dataSource={categoryList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('activityTracking.qrCodeActivityCategory.list')}
        headerBtnDef={
          enableCreate
            ? [
                {
                  color: 'primary',
                  title: Translation('app.button.add'),
                  onClick: () => history.push(`${detailPath}/create`),
                },
              ]
            : []
        }
        operationDef={[
          {
            title: Translation('section.common.operation.view'),
            tooltipText: 'View Category',
            onClick: (row) => history.push(`${detailPath}/view/${row.originId ?? row._id}`),
          },
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Category',
            condition: (row) => enableUpdate && row.status === EStatus.InActive,
            onClick: (row) => history.push(`${detailPath}/edit/${row.originId ?? row._id}`),
          },
          {
            title: Translation('activityTracking.action.publish'),
            tooltipText: 'Publish Category',
            condition: (row) => enableUpdate && row.status === EStatus.InActive,
            onClick: (row) =>
              startTransition(() => {
                handleToggleStatus(row, EStatus.Active);
              }),
          },
          {
            title: Translation('activityTracking.action.unpublish'),
            tooltipText: 'Unpublish Category',
            condition: (row) => enableUpdate && row.status === EStatus.Active,
            onClick: (row) =>
              startTransition(() => {
                handleToggleStatus(row, EStatus.InActive);
              }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('activityTracking.qrCodeActivityCategory.category'),
            renderData: (row) => row.name,
          },
          {
            keyIndex: 'framework',
            displayName: Translation('activityTracking.qrCodeActivityCategory.framework'),
            renderData: (row) => String(row.framework ?? ''),
          },
          {
            keyIndex: 'sortOrder',
            displayName: 'Display Order',
            renderData: (row) => String(row?.sortOrder ?? '-'),
          },
          {
            keyIndex: 'status',
            displayName: Translation('activityTracking.qrCodeActivityCategory.status'),
            renderData: (row) => row.status,
          },
          {
            keyIndex: 'accessibility',
            displayName: Translation('activityTracking.qrCodeActivityCategory.accessibility'),
            renderData: (row) => row.accessibility,
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('activityTracking.qrCodeActivityCategory.createdTime'),
            renderData: (row) => (row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '-'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('activityTracking.qrCodeActivityCategory.lastUpdatedTime'),
            renderData: (row) => (row.updatedAt ? moment(row.updatedAt).format('DD/MM/YYYY HH:mm:ss') : '-'),
          },
          {
            keyIndex: 'lastUpdatedBy',
            displayName: Translation('activityTracking.qrCodeActivityCategory.lastUpdatedBy'),
            renderData: (row) => row.lastUpdatedBy,
          },
        ]}
        totalPages={categoryList?.totalPages}
        totalRecords={categoryList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          const next = { ...filterState, page, limit: rowsPerPage };
          filterStateRef.current = next;
          setFilterState(next);
          refreshData();
        }}
      />
    </>
  );
};

export default QRCodeActivityCategoryListPage;
